/**
 * P3 Node Catalog — catalog-driven node config(對齊 docs/44 P3 FE 整合契約)
 * -----------------------------------------------------------------------------
 * `GET /workflow/catalog` 把「TOOL 節點有哪些參數 / 可選哪些 model / 每 model
 * 多少點」從 FE 寫死改成後端動態回。FE 進 canvas 拉一次 cache 整個 session。
 *
 * **為什麼直接用 snake_case shape 當 domain type、不做 camel mapper**：
 * catalog 是純唯讀消費資料(FE 從不回送 catalog DTO 給 BE),且 key 數量多
 * (`model_selection` / `input_kinds` / `read_only` / `schema_version`)。再寫一層
 * camelCase mapper 只是增加 surface area 而無實益 — 跟 ads / job / me 那種有寫回
 * 路徑、需要 round-trip 的 DTO 不同。唯一 normalize：`models` 缺值補空陣列
 * (見 `mapCatalogResultDto`)讓下游(`creditsForToolWithCatalog`)不必每次 null-guard。
 *
 * config 契約見 docs/44 §2:user 值塞進 `node.config`(typed `extra="forbid"`),
 * UI 文字塞 `display_label`/`display_meta` top-level。這支只定義 catalog 本身。
 */

/**
 * Model 選擇模式(docs/44 §1 / §4)。
 *
 * - `user`:render 下拉,options = `models[]`,預選 `default`;可送 `config.model`。
 * - `auto`:不 render(後端依上游選);送 `config.model` → 422。
 * - `fixed`:不 render(可顯 `models[0].label`);送 `config.model` → 422。
 * - `none`:`models=[]`,不 render;送 `config.model` → 422(COMPOSE)。
 */
export type ModelSelection = 'user' | 'auto' | 'fixed' | 'none';

/**
 * ParamSpec 的型別 union(docs/44 §1)。對應動態表單控件:
 * `float`/`int` → number input(`int` 送值須整數)、`str` → text、`bool` → checkbox、
 * `enum` → select(options 來自 `ParamSpec.enum`,值可能是字串或**數字**如 duration_seconds 的 4/6/8)、
 * `list` → 多字串 / tag 輸入(值是 `string[]`;`ParamSpec.max` = **清單長度上限**非數值上界)。
 */
export type ParamType = 'float' | 'int' | 'str' | 'bool' | 'enum' | 'list';

/**
 * 參數權限分層(docs/44 §1)。
 *
 * - `user`:一般 user 可送。
 * - `admin`:admin 才拿得到 + 可送;一般 user 硬塞 → submit 422。
 *
 * 注意:後端還有 `server` tier(任何人拿不到,後端 allowlist 投影掉),所以 FE
 * 收到的 catalog **不會**出現 `server`,故此 union 不列 `server`。
 */
export type ParamTier = 'user' | 'admin';

/**
 * 一個可選 model(docs/44 §1 ModelOption)。
 *
 * @property id      不透明字串;下拉 value + 送 `config.model` 用。**不可 hardcode**。
 * @property label   顯示名稱。
 * @property default 每個 tool 恰一個為 `true`(user model 下拉預選它)。
 * @property credits per-step 點數(≥1);估價加總用。
 */
export interface ModelOption {
  id: string;
  label: string;
  default: boolean;
  credits: number;
  /**
   * 這顆 model 會不會**原生讀參考影片**(docs/107 A-5;BE S-a,只在 IMAGE 工具投影,
   * SSOT = BE `IMAGE_VIDEO_INPUT_MODELS`)。`undefined` = 舊 BE 尚未外吐此欄 →
   * 讀取端 fallback 到 FE 鏡射清單(`imageVideoRef.IMAGE_VIDEO_INPUT_MODELS`),
   * BE 上齊後那份鏡射即可退場。
   */
  video_input?: boolean;
  /**
   * R351(BE docs/125 §3,2026-09-20 補進投影)— 這顆 model 支援的 `generation_mode`。
   * `null` = 不限;**`undefined` = 舊 BE 還沒 ship 這欄** → 讀取端一律 fail-open
   * (不過濾、不表態),BE 部署後自動生效。值例:lite = `["TEXT","FIRST_FRAME"]`
   * (**不含 `FIRST_LAST`** —— 頭尾幀選它必 422)。
   */
  modes?: string[] | null;
  /**
   * R351 — 這顆 model 支援的解析度(`null` = 不限;`undefined` = 舊 BE)。
   * R385(BE docs/78 禁用表)— **IMAGE 的檔位鍵是 `image_size`**(512px/1K/2K/4K),
   * 值域同樣讀這張表;VIDEO/OMNI 讀的是 `resolution`。
   */
  resolutions?: string[] | null;
  /**
   * R385(BE docs/78 禁用表,`3f9acf4`)— 這顆 model 支援的 `aspect_ratio` 集合。
   * `null` = 不限(= tool param enum 全開);`undefined` = 舊 BE 未投影 → fail-open。
   * 現值:IMAGE pro 只支援舊 10 值(1:4 / 4:1 / 1:8 / 8:1 不收)。
   */
  aspect_ratios?: string[] | null;
  /**
   * R350(Omni 1.1 二拍 ■3)— 解析度分檔點數表(`{"360p":24,"720p":72,…}`;
   * VIDEO_OMNI / VIDEO_EDIT 專屬,`models[].credits` 仍在 = 360p 檔 fallback,
   * **別拿 credits 當唯一價**)。`undefined` = BE 未 ship(v22-77 前)→ 選單旁不顯
   * 點數(fail-soft),分檔上線後自動出現。
   */
  resolution_credits?: Record<string, number>;
}

/**
 * 一個參數規格(docs/44 §1 ParamSpec)。
 *
 * @property key     = `config` key(送 `node.config[key]` 用)。
 * @property type    控件型別。
 * @property tier    權限分層(`user`/`admin`)。
 * @property default `null` = 不預填(用後端預設);非 null 則 `buildInitialConfig` 預填。
 *                   `type='list'` 的 default 是 `string[]`(如 `aspect_ratios` 的 `["1:1"]`)。
 * @property min     數值下界(JSON 一律 float,如 `1.0`;FE 當 number 用)。`null` = 無界。
 * @property max     數值上界(同 min)。**`type='list'` 時 max = 清單長度上限**(元素個數,
 *                   如 style_keywords max=20),不是數值上界;`min` 對 list 恆 `null`。
 * @property enum    `type='enum'` 時的選項;**值可能是數字**(如 duration_seconds 的 `[4,6,8]`)。
 *                   其他 type(含 `list`)為 `null`。
 * @property label   顯示標籤;可能為 `null`(fallback 用 key)。
 */
export interface ParamSpec {
  key: string;
  type: ParamType;
  tier: ParamTier;
  default: string | number | boolean | string[] | null;
  min: number | null;
  max: number | null;
  enum: Array<string | number> | null;
  label: string | null;
  /**
   * docs/76 §7(v0.2)— `"parameter"` = 真 API 參數(檔位/數值 UI)。optional:舊 BE 未給
   * → undefined(FE 當 parameter 處理,現行行為不變)。與 `prompt_fields`(prompt 類業務欄)
   * 對照,見 {@link ToolCapability.prompt_fields}。
   */
  effect?: 'parameter' | 'prompt';
}

/**
 * docs/76 §7(v0.2)— typed-config 業務欄(`creative_type` / `execution_style` / `style` /
 * `lighting` / `tone`…),`effect` 恆 `"prompt"`(底層加字,UI 須明示「會在提示詞加一段描述」)。
 * 不在 `params`(那是真 API 參數);與 params 重疊的欄(tone/locale)只列 params 不重複。
 */
export interface PromptFieldSpec {
  key: string;
  effect: 'prompt';
}

/** docs/76 §7.7 / docs/77 §5(BE-5)— COPY 結構化文案規格(逐類別 enabled/count)。 */
export type CopySpec = Record<string, { enabled?: boolean; count?: number }>;

/**
 * docs/77 §4.5 → **ADR-040 v2(R177)→ BE P-12 投影(範本套用輪)**— 範例/模板庫三目錄
 * (catalog 頂層,按卡型;權威投影 = BE `workflow/templates.py` 的 `templates_*_to_fe`)。
 * **IMAGE/VIDEO 套用 = 只寫 `config.template_id` 一欄**(範本提示詞屬 server 側 REFINE
 * 輸入,刻意不投影);**TEXT(COPY 卡)例外**:文字線不走 REFINE,`prompt` 是 FE 可展示
 * 的 brief 起點、`copy_spec` 是可套用的則數/開關(見 TemplateLibDialog)。
 * `allowed_models`/`variant` 供 FE 過濾選單(submit 期 BE 硬驗 → 422
 * `TEMPLATE_CONSTRAINT_VIOLATION`);其餘 `allowed_*` 目前 null-only(收 doc 暫不硬驗)。
 * `demo_*_urls` = 公開靜態 https 範例素材(「載入範例素材」資料源 — 下載後走 `/uploads`
 * 重新上傳成使用者資產再綁 slots,**不可把 URL 直接當素材**);
 * `preview_url` 是外部 CDN hotlink(顯示用,**勿寫 config**;可能是 mp4 — 影片縮圖用
 * `<video>` 不塞 `<img>`)。全部 optional 資料驅動;目錄未 seed → 空(誠實空態)。
 *
 * **R426(BE docs/139)— 顯示字跟著 `GET /catalog?locale=` 換語系**(四種範本都是):`title`
 * (格式照舊「主標|副標」)、`tags[]`、reel・文案的 `prompt`(brief 起點)是該語系的字;
 * `category` **維持繁中原值**(分組 / 篩選 / 排序的穩定鍵),顯示改用 `category_label`
 * (`templateCategoryLabel`)。`id` / URL / `allowed_*` / `demo_*` 不分語系。
 */
export interface ImageTemplate {
  id: string;
  /** 分類的**穩定鍵**(繁中原值,不隨語系變;分組 / 篩選用,顯示走 `category_label`)。 */
  category: string;
  /** R426(docs/139)— 分類顯示名稱(該語系;繁中時 = `category`;舊後端缺欄 → 退 `category`)。 */
  category_label?: string | null;
  title: string;
  preview_url: string | null;
  /** BE P-12 — 篩選標籤(目前僅顯示/未來篩選)。 */
  tags?: string[] | null;
  /** v1 遺留(英文起點文案;v2 IMAGE 投影不再吐,僅容錯保留)。 */
  prompt?: string;
  /** ADR-040 v2 — 套用後模型白名單(缺/空 = 不過濾)。 */
  allowed_models?: string[] | null;
  /** BE P-12 — 比例白名單(null = 不限;暫 null-only,FE 先不過濾)。 */
  allowed_aspect_ratios?: string[] | null;
  /** BE P-12 — 固定尺寸白名單(null = 不限;暫 null-only)。 */
  allowed_fixed_sizes?: string[] | null;
  /** BE P-12(B 案)— 範例產品圖(公開 https;null = 未備 → 不顯「載入範例素材」)。 */
  demo_product_urls?: string[] | null;
  /** BE P-12(B 案)— 範例場景圖(同上)。 */
  demo_scene_urls?: string[] | null;
  /**
   * docs/85(2026-08-05)— 範例模特兒 = `characters[]` 的 id;非 null → 「載入範例素材」
   * 時自動選定該 AI 模特兒(寫卡 `character_id` + reference_urls 下載重傳綁
   * `character_asset_ids`,等同使用者自己在名冊點了那位)。**三個 demo 鍵恆在 payload,
   * 沒有該類素材 = 明確 null**(FE 型別仍 optional 容錯舊 catalog)。
   */
  demo_character_id?: string | null;
}
export interface VideoTemplate {
  id: string;
  /** 穩定鍵(同 `ImageTemplate.category`)。 */
  category: string;
  /** R426 — 分類顯示名稱(同 `ImageTemplate.category_label`)。 */
  category_label?: string | null;
  title: string;
  preview_url: string | null;
  /**
   * R278(BE 2026-08-22,docs/96 頂部更新段)— 合併清單判別欄:`templates_video[]`
   * 現在 = **reel 範本(在前)∪ video 範本**,每筆帶 `"VIDEO_REEL"` / `"VIDEO"`。
   * optional 容錯舊 catalog(缺 = 當 VIDEO);判讀走 `templateIsReel`,**開放字串**
   * (BE 之後併入第三種卡型時,不認得的值 fail-safe 走一般套用,不誤觸建卡轉換)。
   */
  tool_type?: string;
  /** BE P-12 — 篩選標籤。 */
  tags?: string[] | null;
  aspect_ratio?: string;
  duration?: number | null;
  resolution?: string | null;
  funnel?: string[];
  logline?: string;
  prompt?: string;
  /** ADR-040 v2 — 套用後模型白名單(缺/空 = 不過濾)。 */
  allowed_models?: string[] | null;
  /** BE P-12 — 比例/解析度/秒數白名單(null = 不限;暫 null-only,FE 先不過濾)。 */
  allowed_aspect_ratios?: string[] | null;
  allowed_resolutions?: string[] | null;
  allowed_durations?: number[] | null;
  /** BE P-12(B 案)— 範例素材(同 ImageTemplate;docs/85 起含 demo_character_id)。 */
  demo_product_urls?: string[] | null;
  demo_scene_urls?: string[] | null;
  demo_character_id?: string | null;
  /**
   * R350.7(docs/85 §3.2,2026-09-17)— 第四類範例素材 → 綠卡「其他/額外參考」格。
   * 語意 = 風格/情境線索(不鎖產品外觀、不鎖場地);**omni 影片範本的九宮格分鏡走這格**。
   * ⚠️ BE 只有 `TemplateVideo` 帶這欄(image/reel 投影沒有),所以只宣告在這裡。
   */
  demo_extra_urls?: string[] | null;
  /** ADR-040 v2 — 範本綁定的 VIDEO variant(缺 = 不限;submit 期不符 → 422)。 */
  variant?: string;
}
export interface TextTemplate {
  id: string;
  /** 穩定鍵(同 `ImageTemplate.category`)。 */
  category: string;
  /** R426 — 分類顯示名稱(同 `ImageTemplate.category_label`)。 */
  category_label?: string | null;
  title: string;
  /**
   * BE P-12 — COPY 範本起點文案(文字線不走 REFINE,套用時預填 brief 可編輯)。
   * R426 起是 catalog 請求語系的字(docs/139;舊後端一律繁中)。
   * IMAGE/VIDEO 沒有此欄是刻意的(範本提示詞不外流)。
   */
  prompt?: string | null;
  /** v1 遺留 preset 欄(v2 投影不再吐;容錯保留給舊 catalog 的卡面預覽 fallback)。 */
  core_message?: string;
  tone?: string;
  creative_type?: string;
  funnel_stage?: string;
  target_audience?: string;
  /** COPY 結構化文案規格 — 套用時寫進 `config.copy_spec`(§J 合法欄,text-gen 真消費)。 */
  copy_spec?: CopySpec | null;
  /** ADR-040 v2 — 套用後模型白名單(缺/空 = 不過濾)。 */
  allowed_models?: string[] | null;
}

/**
 * R272(ADR-045 / docs/96 §1)— 動態圖文範本目錄項(catalog 頂層 `templates_reel`;
 * 權威投影 = BE `templates.templates_reel_to_fe`)。
 *
 * 與 IMAGE/VIDEO 範本的**關鍵差異**:`prompt` 是**brief 起點(R426 起依 catalog 語系)且刻意投影 FE**
 * (reel 不走 PROMPT_REFINE — 導演提示詞在 server 側 `reel/prompts.py`,範本 prompt
 * 只是給使用者改寫的起點,無外洩面)。語意同 {@link TextTemplate.prompt}。
 *
 * `style_id` **必投**:套範本時 FE **不要**顯式送 `config.style_id`(BE submit 期以
 * 範本值正規化;顯式不符 → 422 `TEMPLATE_CONSTRAINT_VIOLATION`),這一欄只用來
 * 「顯示這個範本是什麼風格」與鎖住風格選單。
 *
 * `preview_url` 是公開 bucket 的樣片 **MP4**(0.4–1.7MB)→ 選卡一律 `<video>` 不塞
 * `<img>`(`isVideoPreviewUrl` 已處理)。demo 三欄恆在 payload(沒有該類 = 明確 null;
 * reel 無場景槽 / 無模特兒 → 後兩欄恆 null,欄位存在是 `import-template-demo` 契約)。
 */
export interface ReelTemplate {
  id: string;
  /** 穩定鍵(同 `ImageTemplate.category`)。 */
  category: string;
  /** R426 — 分類顯示名稱(同 `ImageTemplate.category_label`)。 */
  category_label?: string | null;
  title: string;
  preview_url: string | null;
  /** R278 — 同 `VideoTemplate.tool_type`(reel 條目恆 `"VIDEO_REEL"`;兩個 key 同一份投影)。 */
  tool_type?: string;
  tags?: string[] | null;
  /** 導演風格(= `VideoReelConfig.style_id` 值域)。 */
  style_id?: string;
  /** brief 起點(套用時預填 `text_value`,brief 為空才填;R426 起是 catalog 請求語系的字)。 */
  prompt?: string;
  demo_product_urls?: string[] | null;
  /** reel 無場景槽 → 恆 null(欄位存在是 template_demo 契約)。 */
  demo_scene_urls?: string[] | null;
  /** reel 無模特兒 → 恆 null(同上)。 */
  demo_character_id?: string | null;
}

/**
 * docs/77 §1 / docs/76 §7.6(v0.7 / ADR-038 BE-3)— 固定尺寸目錄(catalog 頂層)。IMAGE
 * 綠卡「輸出規格」勾選 UI 資料源;勾選結果 `id` 寫進 config `fixed_sizes`(≤12)。
 */
export interface ImgSize {
  id: string;
  label: string;
  width: number;
  height: number;
  recommended: boolean;
  category: 'LOGO' | 'GDN';
}

/**
 * TOOL 節點的 input_kinds(docs/44 §1)。描述該 tool 接受哪些上游 media + 是否
 * 收 text。FE Build phase 用來決定要 render 哪些上游連線提示 / text 欄位。
 */
export interface ToolInputKinds {
  media: string[];
  text_fold: boolean;
}

/**
 * 一個 TOOL 的能力描述(docs/44 §1 — `tools[tool_type]`)。
 *
 * @property label           顯示名稱(如「文案」)。
 * @property model_selection 見 {@link ModelSelection}。
 * @property models          可選 model 陣列(COMPOSE / `none` 為 `[]`)。
 * @property params          動態表單參數規格。
 * @property input_kinds     接受的上游 media + text 設定。
 */
export interface ToolCapability {
  label: string;
  model_selection: ModelSelection;
  models: ModelOption[];
  params: ParamSpec[];
  input_kinds: ToolInputKinds;
  /**
   * docs/76 §7(v0.2)— typed-config prompt 類業務欄(creative_type/execution_style/…)。
   * optional:舊 BE 未給 → 消費端 `?? []`。UI 依此對 prompt 欄掛「加字」ⓘ 說明。
   */
  prompt_fields?: PromptFieldSpec[];
  /**
   * ADR-039 影片卡統一(BE §1)— `VIDEO` 的 variant 目錄(single VIDEO 節點 + `config.variant`)。
   * 只有 VIDEO 會給;缺某 variant = 該版不支援 → UI 不顯。FE 動態讀,**不寫死** variant 清單。
   */
  variants?: VideoVariantSpec[];
  /**
   * R284(docs/103 §1.1)— FE **新建卡**預設選中的 variant id。BE 投影期算(code-owned
   * 偏好序 `VIDEO_DEFAULT_VARIANT_ORDER`;2026-09-16 隨 D/E・direct 下架後現值只剩
   * `general`,flag 過濾後永遠指向一個真的在 `variants[]`
   * 裡的 id),並保證 `default_variant === variants[0].id`。其他 tool 恆 `null`、
   * `variants` 恆 `[]`。optional:舊 BE 未給 → undefined(消費端退 R174 的 `direct`,
   * 見 `defaultVideoVariantFromCatalog`)。
   */
  default_variant?: string | null;
  /**
   * R350(BE `a9bdbb9c`)— VIDEO_OMNI 專屬:參考素材數值上限的機器可讀投影
   * (`{image_max: 10, video_max: 1, video_max_seconds: 10.0}`;值的 SSOT = BE executor
   * 常數)。`undefined` = 其他 tool / 舊 BE → 消費端退 FE 鏡射常數
   * (`subjectSlots.MAX_REFERENCE_*_OMNI`,見 `omniReferenceLimits`)。
   */
  reference_limits?: OmniReferenceLimits;
}

/**
 * R350 — `tools.VIDEO_OMNI.reference_limits` 投影塊(BE `_omni_reference_limits`)。
 * ADR-057 — `tools.IMAGE.reference_limits` 新增,同一個形狀(docs/140 §2)。
 * R399(ADR-052)— `tools.VIDEO_SEEDANCE.reference_limits` 共用同一個形狀
 * (BE `_seedance_reference_limits` 多投影兩欄:單支秒數**下限**與**總長**上限;
 * omni 投影沒有這兩欄 → undefined,消費端各自退鏡射常數)。
 */
export interface OmniReferenceLimits {
  image_max?: number;
  video_max?: number;
  video_max_seconds?: number;
  /** R399 — 單支參考影片秒數下限(seedance 官方 [2,15];omni 無此欄)。 */
  video_min_seconds?: number;
  /** R399 — 全部參考影片總秒數上限(seedance 官方 ≤15;omni 無此欄)。 */
  video_total_max_seconds?: number;
  /** docs/132 v0.5 — 卡片輸入來源總數上限(超過 submit 422 `GREEN_FANIN_EXCEEDED`)。 */
  fanin_max?: number;
  /**
   * docs/132 v0.5 — `true` = 圖片先壓四合一格網(Seedance 2.0 系);`false` = 原圖直送(2.5)。
   * ⚠️ ADR-057 起 BE 恆 `false` 並在下一版刪除;**FE 不讀這欄**(全 repo 零消費端,留型別
   * 只為了讀得懂舊回應)。
   */
  uses_grid?: boolean;
  /**
   * ADR-057 / docs/140 §2 — 單張參考圖上傳上限(bytes,現值 30 MiB)。IMAGE / VIDEO_OMNI /
   * VIDEO_SEEDANCE 新 BE 才有;缺欄 = 舊 BE → 消費端維持舊上限(`cardImageMaxBytes`)。
   */
  image_max_bytes?: number;
  /** ADR-057 — 該 model 收的最小邊長(px;字節線才有,其餘 null)。 */
  image_min_edge_px?: number | null;
  /** ADR-057 — 該 model 收的最小像素數(字節線才有,其餘 null)。 */
  image_min_pixels?: number | null;
  /** ADR-057 — 前端合成可用的版面(現值 `["1x2","2x2","3x3"]`);缺欄 = 舊 BE(intent 不收 `composite`)。 */
  composite_layouts?: string[];
  /**
   * docs/132 v0.5 — **依 model 的上限**(seedance 2.0 系 / 2.5 不同)。頂層值 = catalog
   * default model 的值(2026-10-22 起 = 2.5);卡上選了哪顆就讀 `by_model[model_id]`。
   * omni 無此欄。
   */
  by_model?: Record<string, Omit<OmniReferenceLimits, 'by_model'>>;
}

/**
 * ADR-039 影片卡 variant 規格(catalog `tools.VIDEO.variants[]`)。
 *
 * @property id                  variant key(`general`/`direct`/`endframes`/`extend`)。
 * @property label               顯示名稱(如「素材直生」)。
 * @property description         副標/說明(卡面 tag 短標由 FE i18n,長描述用這)。
 * @property required_asset_field 該 variant 必備素材的 config 欄名(`reference_image_asset_ids`/
 *   `frame_asset_ids`);無必備素材(如 general)→ null / 省略。
 * @property min_assets/max_assets 必備素材數量下/上限(direct 1–3;endframes 3–3)。
 * @property variant_fields      該 variant 專屬的 config 欄 key(切走時 FE 清這些)。
 * @property aspect_ratios       該 variant 支援的比例(缺 → 用共用/契約預設)。
 * @property segment_plan        docs/78 §H-4(v3)— 分鏡骨架(寫死;各段 role/秒數/max_scenes)。
 *   FE 據此渲染**固定段容器**(direct/endframes 強制兩段);缺 → storyboard UI fail-closed
 *   (= BE 未 ship §I;`videoStoryboardEnabled`)。新模型加卡 = 加 variant + segment_plan 宣告。
 */
export interface VideoVariantSpec {
  id: string;
  label: string;
  description?: string;
  required_asset_field?: string | null;
  min_assets?: number;
  max_assets?: number;
  variant_fields?: string[];
  aspect_ratios?: string[];
  segment_plan?: SegmentPlan[];
}

/**
 * docs/78 §H-4(v3)— 固定流程卡的一段分鏡骨架。
 *
 * @property role       段角色(`clip`/`base`/`extend`/`seg1`/`seg2`…;顯示 label 由 FE i18n)。
 * @property seconds    該段秒數;`null` = 跟隨 `duration_seconds`(general 單段)。
 * @property max_scenes 該段最多幾顆 beat(超額 FE 灰態,忽略尾端多餘)。
 */
export interface SegmentPlan {
  role: string;
  seconds: number | null;
  max_scenes: number;
}

/**
 * docs/78 §H-3(c)/ §I-3 #4 — 貼上解析關鍵字表(catalog 頂層投影 `prompt_parse_keywords`,
 * server SSOT)。FE 用**同一份表**做即時連動解析(貼上提示詞 → 切區塊/beat),不寫死不雙份漂移。
 * 結構:`blocks`(五區塊級)/ `beat_fields`(beat 級五要素 + 音訊),各 key 帶 en/zh/ja alias。
 * 缺(舊 BE / 未 ship)→ FE 退契約靜態表(storyboardParse `FALLBACK_KEYWORDS`)。
 */
export interface PromptParseKeywordAliases {
  en?: string[];
  zh?: string[];
  ja?: string[];
}
export interface PromptParseKeywords {
  blocks: Record<string, PromptParseKeywordAliases>;
  beat_fields: Record<string, PromptParseKeywordAliases>;
}

/**
 * R424(BE docs/137 §2.1)— 廣告文案單一類別的字數 / 則數界(`copy_gen_bounds.categories[cat]`)。
 *
 * 兩條字數上限,單位都是 **Google 半形字元**(全形 2、半形 1;計字 = `lib/copyLength.adCharLen`):
 *   - `char_limit` = **生成上限**(AI 照這條寫;文案卡 / 藍卡 / 畫布燈箱顯示這條)。
 *   - `reject_char_limit` = **退件線**(素材庫文案編輯 `PUT …/text-content` 與檢查報告驗這條;
 *     目前只有 CTA 不同:生成 10 / 退件 30)。
 * `*_full_width_limit` = 同一條上限換成純中文 / 日文最多幾個字(BE `char_limit // 2`,奇數捨去)。
 *
 * ⚠️ 新欄位 optional:`full_width_limit` / `reject_*` 是 BE `dev-update-v22-203` 才有的欄;
 * 舊 BE 缺欄由 `features/canvas/copyGenBounds` 處理(缺退件線 = 不顯上限,**不猜**)。
 * BE 對未知類別的 `reject_char_limit` 回 `0` = 無資料,不是「上限 0」。
 */
export interface CopyGenCategoryBounds {
  char_limit: number;
  full_width_limit?: number;
  reject_char_limit?: number;
  reject_full_width_limit?: number;
  count_min: number;
  count_max: number;
  soft_min: number;
}

/**
 * R424 — `GET /workflow/catalog` → `result.copy_gen_bounds`(BE `bounds_to_fe`;admin 可在
 * `system_config/copy_gen` 調生成上限,所以**數字一律讀這裡,不寫死**)。
 *
 * hint 兩組:描述「至少一則少於 `description_short_hint` 字元」、短標題「至少一則 ≤
 * `short_title_pmax_hint` 字元」;`*_full_width` 為對應的純中文字數(描述是「少於 60」→ 29)。
 */
export interface CopyGenBounds {
  categories: Record<string, CopyGenCategoryBounds>;
  description_short_hint: number;
  description_short_hint_full_width?: number;
  short_title_pmax_hint: number;
  short_title_pmax_hint_full_width?: number;
}

/**
 * catalog 頂層 feature / 能力宣告。
 *
 * R424(BE docs/137 §0)— `output_locale` = 這個環境的後端已收 `locale`(assist / text-gen /
 * copy-assist / jobs 四支)。**能力宣告,不是 feature flag**(同 `logo_slot`):部署到哪個環境
 * 那裡就恆 true。缺 = 舊後端 → **不送**(四支都是 `extra="forbid"`,送了整包 422)。
 */
export interface CatalogFeatures {
  mention_refs?: boolean;
  mention_ref_tool_types?: string[] | null;
  logo_slot?: boolean;
  output_locale?: boolean;
  /**
   * R424.1(BE docs/137 §5)— 素材檢查(`POST /asset_check/jobs`、單素材重檢)收 `locale`。
   * ⚠️ **與 `output_locale` 分開**:BE v22-203/204 有 `output_locale` 但素材檢查還不收 `locale`
   * (v22-205 起才收)—— 共用一顆 = 素材檢查 422。缺 = 不送。
   */
  asset_check_locale?: boolean;
}

/**
 * 三色節點(ROOT / OUTPUT)的單一欄位規格(docs/44 §1 NodeFieldSpec)。
 *
 * @property key       欄位 key(如 `text_value` / `asset_id` / `display_label`)。
 * @property type      欄位型別(目前皆 `str`,但保留 union 對齊 catalog)。
 * @property label     顯示標籤。
 * @property read_only `true` = server-owned;FE 唯讀、**不送 PATCH**(如 OUTPUT.asset_id)。
 */
export interface NodeFieldSpec {
  key: string;
  type: ParamType;
  label: string;
  read_only: boolean;
}

/**
 * 一個三色節點(ROOT / OUTPUT)的欄位集合(docs/44 §1 — `nodes[kind]`)。
 */
export interface NodeFieldsCapability {
  fields: NodeFieldSpec[];
}

// ─── ADR-026 canvas AI-assist 擴充 ───────────────────────────────────────────

/** 一個 copy-assist 切角(ADR-026 §1 — `copy_assist.angles[]`)。只有 id + label,模板在後端。 */
export interface CopyAssistAngle {
  id: string;
  label: string;
}

/**
 * copy-assist 切角清單 + feature flag(ADR-026 §1 — `copy_assist`)。
 *
 * - `enabled=false`(prod 預設)→ FE **隱藏**「自動產生文案」入口(非 disable);後端也會 403 雙保險。
 * - `angles` admin runtime 可調,FE 一律以此為準、**不寫死**。
 */
export interface CopyAssistCatalog {
  enabled: boolean;
  angles: CopyAssistAngle[];
}

/**
 * 一個投放版位(ADR-026 §1 — `ad_placements[]`)。fan-out 時每個 ratio 生一張 green IMAGE。
 *
 * @property ratios 該版位的長寬比清單(如 PMax 的 `["1:1","16:9","9:16"]`);上限 8(後端 `PLACEMENT_TOO_MANY_RATIOS`)。
 */
export interface AdPlacement {
  id: string;
  label: string;
  ratios: string[];
}

/**
 * `GET /workflow/catalog` 的 `result`(docs/44 §1)。
 *
 * - `tools` key = tool_type **大寫**(`COPY`/`IMAGE`/`VIDEO`/`COMPOSE`)。
 * - `nodes` key = 節點種類大寫(`ROOT`/`OUTPUT`)。
 *
 * 這就是 domain type(無 camel mapper);FE 直接消費。
 */
export interface Catalog {
  schema_version: number;
  tools: Record<string, ToolCapability>;
  nodes: Record<string, NodeFieldsCapability>;
  /**
   * BE docs/131 §8(R395;ADR-051)— 頂層 feature flags。**optional**(舊 BE 缺欄):
   * `mention_refs` 缺 / false = **fail-closed 藏 @ picker**(token 會以亂碼漏進 prompt,
   * 比沒功能糟);取名 UI(aliases)不受此 flag 限(BE:存而不用、恆可寫)。
   * dev/staging 預設開;beta/prod 等 owner flip(referral 同款模式,admin ≤45s 傳播)。
   *
   * R399.2(docs/131 v0.4;BE dev tag v22-149)— `mention_ref_tool_types` = @ 受理卡型
   * **權威清單**(BE SSOT `MENTION_SUPPORTED_TOOL_TYPES` 投影;VIDEO_SEEDANCE 隨
   * `seedance_pack` flag 自動進出)。FE `supportsAtReference` 讀此欄,勿硬編;
   * **缺欄(v22-149 前的 BE)→ 退 R399.1 的卡型全開**(owner 拍板的知情例外)。
   *
   * R404 — `logo_slot` = BE 已受理 `slots.logo_asset_ids`(獨立 Logo 格;docs/54 §R404 的
   * **FE 提案**,BE 定案時以 BE 為準)。缺 / false = **fail-closed 不長 Logo 格**:BE
   * `NodeSlots` 是 `extra="forbid"`,未開欄前送這個鍵 = 整包 slots 422(產品 / 模特兒一起存不進去)。
   */
  features?: CatalogFeatures;
  /**
   * ADR-026 copy-assist 切角 + feature flag。**optional**:prod 預設 OFF / 舊 BE 可能漏給。
   * `mapCatalogResultDto` 缺值 normalize 成 `{enabled:false, angles:[]}`;消費端仍以
   * `catalog.copy_assist?.enabled` 派生、`enabled=false` 隱藏入口。
   */
  copy_assist?: CopyAssistCatalog;
  /**
   * ADR-026 投放版位選單。**optional**:舊 BE 可能漏給;缺值 normalize 成 `[]`。
   * 消費端讀 `catalog.ad_placements ?? []`,**不寫死**版位 / 比例上限。
   */
  ad_placements?: AdPlacement[];
  /**
   * docs/76 §7.3(v0.2 / ADR-035)— 執行風格 preset 目錄(頂層)。**optional**:舊 BE 缺 →
   * normalize 成 `[]`(消費端據此 gate:非空才顯執行風格下拉)。
   */
  /**
   * docs/77 §4.5(v0.8 / ADR-038 BE-10)— 範例/模板庫三目錄(按卡型)。**optional**:
   * 平台未 seed → normalize 成 `[]`(選卡 dialog 顯示空)。
   */
  templates_image?: ImageTemplate[];
  templates_video?: VideoTemplate[];
  templates_text?: TextTemplate[];
  /**
   * R272(ADR-045)— 動態圖文範本目錄。**optional**:flag `video_reel` off 時 BE 回 `[]`
   * (整包不投影),舊 BE 直接漏給 → normalize 成 `[]`(選卡 dialog 誠實空態)。
   */
  templates_reel?: ReelTemplate[];
  /**
   * docs/77 §1 / docs/76 §7.6(v0.7 BE-3)— 固定尺寸目錄。**optional**:舊 BE 缺 →
   * normalize 成 `[]`(輸出尺寸勾選 UI 資料源)。
   */
  img_sizes?: ImgSize[];
  /**
   * docs/77 §1+§4(BE-2)— AI 模特兒庫(平台策展)。**optional**:初期可能空陣列
   * (admin 未上架)→ 選人區顯示空。選人 = 寫 config `character_id` + `character_fragment`
   * (= `persona_fragment` 原樣);參考圖依 `library_ref` 走素材庫匯入 → `slots.character_asset_ids`。
   */
  characters?: CatalogCharacter[];
  /**
   * docs/78 §H-3(c)/ §I-3 #4 — 貼上解析關鍵字表(server SSOT)。**optional**:BE 未 ship
   * storyboard(§I)→ undefined;FE 退契約靜態表。**同時是 storyboard UI 的 ship 訊號之一**
   * (與 `variants[].segment_plan` 一起 ship;見 `videoStoryboardEnabled`)。
   */
  prompt_parse_keywords?: PromptParseKeywords;
  /**
   * R424(BE docs/137 §2.1)— 廣告文案字數 / 則數界。BE 自 dev-update-v4 起就有投影,但 R424 前
   * mapper 沒透傳(FE 一直用寫死的 `COPY_GROUPS`)。**passthrough**,缺 → undefined(消費端不顯數字)。
   */
  copy_gen_bounds?: CopyGenBounds;
}

/**
 * docs/77 §1(BE-2)→ **ADR-040 v2(R177)**— AI 模特兒目錄項。
 * R426(docs/139)— `label` / `tags[]` 是 catalog 請求語系的字(同一個詞全份同一種譯法,
 * 所以以 `tags[0]` 分組在任何語系結果都一樣);`id` / URL 不分語系。
 */
export interface CatalogCharacter {
  id: string;
  label: string;
  /** 人像縮圖(https;可 null → fallback 樣式)。 */
  preview_url?: string | null;
  /**
   * v1 遺留(英文人物描述)。ADR-040 v2 投影**不再吐**(`character_fragment` 已整層
   * 退場,選人只寫 `character_id`);optional 容錯保留給舊 catalog。
   */
  persona_fragment?: string;
  /** ADR-040 v2 — 篩選標籤(取代 persona_fragment 的目錄角色)。 */
  tags?: string[];
  /**
   * 參考圖素材庫指標(nullable;匯入轉 project asset 用)。⚠️ 素材庫是 group 專屬 —
   * 此欄只在「該 group 自己策展的庫」內成立,平台級參考照改用 `reference_urls`。
   */
  library_ref?: string | null;
  /**
   * BE P-12(B 案)— 模特兒參考照(公開靜態 https;≤9)。「載入參考照」資料源:
   * 下載後走既有 `/uploads` 上傳成使用者資產再綁 `slots.character_asset_ids`
   * (與 persona 文字雙軌)。null = 只有 persona 文字 → 不顯載入鈕。
   */
  reference_urls?: string[] | null;
}

/**
 * docs/76 §7.2(v0.2)— `creative_type` 六選一(v1)。= assist request 合法值;
 * 未知值 assist 422 `ASSIST_UNKNOWN_CREATIVE_TYPE`。UI chip 列 + i18n key `creativeType.<id>`。
 */
export const CREATIVE_TYPES = [
  'UNBOXING',
  'FEATURE_INTRO',
  'PAIN_SOLUTION',
  'UGC_REVIEW',
  'TUTORIAL',
  'PROMO',
] as const;
export type CreativeType = (typeof CREATIVE_TYPES)[number];

/**
 * BE wire 上的 `result` shape — 跟 {@link Catalog} 同 snake-case,但每個 tool 的
 * `models` 可能漏給(catalog doc 壞 / fail-open BE 省略)。`mapCatalogResultDto`
 * 把缺值補成 `[]`。其餘欄位 BE 契約保證齊全,直接 passthrough。
 *
 * 我們仍宣告一個獨立 DTO 型別讓「BE 可能給 partial」這件事在型別上顯式 —
 * `models?` optional 表達「可能缺」,normalize 後的 domain type 則是 required `[]`。
 */
export interface CatalogResultDto {
  schema_version: number;
  tools: Record<string, ToolCapabilityDto>;
  nodes: Record<string, NodeFieldsCapability>;
  /** BE docs/131 §8(R395/R399.2)— mention flag + 受理卡型投影;R404 `logo_slot`(FE 提案);舊 BE 漏給 → passthrough undefined。 */
  features?: CatalogFeatures;
  /** ADR-026 — 可能漏給(prod OFF / 舊 BE);normalize 後 domain 端永遠有值。 */
  copy_assist?: CopyAssistCatalog;
  /** ADR-026 — 可能漏給;normalize 成 `[]`。 */
  ad_placements?: AdPlacement[];
  /** docs/76 §7.3 — 可能漏給;normalize 成 `[]`。 */
  /** docs/77 §4.5(v0.8 BE-10)— 範例/模板庫三目錄;可能漏給,normalize 成 `[]`。 */
  templates_image?: ImageTemplate[];
  templates_video?: VideoTemplate[];
  templates_text?: TextTemplate[];
  /** R272(ADR-045)— 動態圖文範本目錄;flag off / 舊 BE → 漏給,normalize 成 `[]`。 */
  templates_reel?: ReelTemplate[];
  /** docs/77 §1 / docs/76 §7.6(BE-3)— 固定尺寸目錄;可能漏給,normalize 成 `[]`。 */
  img_sizes?: ImgSize[];
  /** docs/77 §1(BE-2)— AI 模特兒庫;可能漏給/空(未策展),normalize 成 `[]`。 */
  characters?: CatalogCharacter[];
  /** docs/78 §I-3 #4 — 貼上解析關鍵字表;可能漏給(未 ship storyboard)→ passthrough undefined。 */
  prompt_parse_keywords?: PromptParseKeywords;
  /** R424 — 文案字數 / 則數界;passthrough(缺 → undefined)。 */
  copy_gen_bounds?: CopyGenBounds;
}

/** 同 {@link ToolCapability} 但 `models` optional(BE 可能漏給)。 */
export interface ToolCapabilityDto {
  label: string;
  model_selection: ModelSelection;
  models?: ModelOption[];
  params: ParamSpec[];
  input_kinds: ToolInputKinds;
  /** docs/76 §7(v0.2)— prompt 類業務欄;可能漏給,domain passthrough(消費端 `?? []`)。 */
  prompt_fields?: PromptFieldSpec[];
  /** ADR-039 — VIDEO variant 目錄;可能漏給(舊 BE / 非 VIDEO)→ undefined。 */
  variants?: VideoVariantSpec[];
  /** R284 — 建卡預設 variant;可能漏給(舊 BE)→ undefined,非 VIDEO 恆 `null`。 */
  default_variant?: string | null;
  /** R350 — VIDEO_OMNI 參考素材上限投影;可能漏給(其他 tool / 舊 BE)→ undefined。 */
  reference_limits?: OmniReferenceLimits;
}

/**
 * 唯一 normalize:把每個 tool 缺漏的 `models` 補成 `[]`。
 *
 * 為什麼只 normalize 這個:`creditsForToolWithCatalog` / model 下拉 render 都會
 * iterate `models`;若 BE fail-open 省略它,下游不必每處 `?? []`。其餘欄位
 * (`params` / `input_kinds` / `nodes`)BE 契約保證齊全,不動。
 */
export function mapCatalogResultDto(dto: CatalogResultDto): Catalog {
  const tools: Record<string, ToolCapability> = {};
  for (const [toolType, cap] of Object.entries(dto.tools)) {
    tools[toolType] = {
      label: cap.label,
      model_selection: cap.model_selection,
      models: cap.models ?? [],
      params: cap.params,
      input_kinds: cap.input_kinds,
      // docs/76 §7 — prompt_fields 可能漏給;passthrough(缺 → undefined,消費端 `?? []`)。
      ...(cap.prompt_fields !== undefined ? { prompt_fields: cap.prompt_fields } : {}),
      // ADR-039 — VIDEO variants passthrough(缺 → undefined;消費端 fallback 契約靜態表)。
      ...(cap.variants !== undefined ? { variants: cap.variants } : {}),
      // R284 — default_variant passthrough(缺 = 舊 BE → undefined;消費端退 `direct`)。
      // 刻意不 normalize:undefined(舊 BE)與 null(非 VIDEO tool)是兩個不同的訊號。
      ...(cap.default_variant !== undefined ? { default_variant: cap.default_variant } : {}),
      // R350 — VIDEO_OMNI reference_limits passthrough(缺 → undefined;消費端退鏡射常數)。
      ...(cap.reference_limits !== undefined ? { reference_limits: cap.reference_limits } : {}),
    };
  }
  return {
    schema_version: dto.schema_version,
    tools,
    nodes: dto.nodes,
    // docs/131 §8(R395)— features passthrough(缺 → undefined;消費端 fail-closed:
    // `supportsAtReference` 只認 `mention_refs === true`)。刻意不 normalize 成
    // `{mention_refs:false}`:undefined = 「舊 BE 未 ship」訊號,語意同 prompt_parse_keywords。
    ...(dto.features !== undefined ? { features: dto.features } : {}),
    // ADR-026 — 缺值 normalize(對齊上面 models ?? [] 紀律):copy_assist 缺 → 隱藏入口的
    // 安全預設 {enabled:false, angles:[]};內層 angles 也 normalize 防 BE 給 enabled 漏 angles。
    copy_assist: {
      enabled: dto.copy_assist?.enabled ?? false,
      angles: dto.copy_assist?.angles ?? [],
    },
    ad_placements: dto.ad_placements ?? [],
    // docs/77 §4.5(BE-10)— 範例/模板庫三目錄缺 → [](選卡 dialog 顯示空)。
    templates_image: dto.templates_image ?? [],
    templates_video: dto.templates_video ?? [],
    templates_text: dto.templates_text ?? [],
    // R272(ADR-045)— flag `video_reel` off 時 BE 就是回 `[]`;缺值同樣 normalize
    // (「沒有範本」與「功能沒開」在 FE 都是同一件事:選卡清單空)。
    templates_reel: dto.templates_reel ?? [],
    img_sizes: dto.img_sizes ?? [],
    // docs/77 §1(BE-2)— characters 缺/未策展 → [](選人區顯示空)。
    characters: dto.characters ?? [],
    // docs/78 §I-3 #4 — prompt_parse_keywords passthrough(缺 → undefined;消費端退靜態表 +
    // 據其有無 gate storyboard UI)。刻意不 normalize 成空物件:undefined = 「未 ship」訊號。
    ...(dto.prompt_parse_keywords !== undefined
      ? { prompt_parse_keywords: dto.prompt_parse_keywords }
      : {}),
    // R424 — copy_gen_bounds passthrough(缺 → undefined;消費端不顯數字,不退寫死表)。
    ...(dto.copy_gen_bounds !== undefined ? { copy_gen_bounds: dto.copy_gen_bounds } : {}),
  };
}

